import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, Hospital, Loader2, Plus, ShieldCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'];

const ORG_TYPES = [
  { v: 'hospital', l: 'Hospital' },
  { v: 'health_system', l: 'Health system' },
  { v: 'snf', l: 'Skilled nursing facility' },
  { v: 'home_health', l: 'Home health agency' },
  { v: 'agency', l: 'Agency / provider' },
  { v: 'payer', l: 'Payer' },
  { v: 'other', l: 'Other' },
];

const CreateOrgModal = ({ open, onClose, onCreated }) => {
  const { getAuthHeader } = useAuth();
  const empty = {
    name: '', type: 'hospital', npi: '', ccn: '',
    primary_state: '', baseline_30d_readmit_rate: '',
    contact_email: '',
  };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...form };
      if (!body.npi) delete body.npi;
      if (!body.ccn) delete body.ccn;
      if (!body.primary_state) delete body.primary_state;
      if (!body.contact_email) delete body.contact_email;
      if (body.baseline_30d_readmit_rate === '') {
        delete body.baseline_30d_readmit_rate;
      } else {
        body.baseline_30d_readmit_rate = parseFloat(body.baseline_30d_readmit_rate);
      }
      await axios.post(`${API}/organizations`, body, getAuthHeader());
      toast.success('Organization created');
      setForm(empty);
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create org');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="spine-org-modal">
        <DialogHeader>
          <DialogTitle className="cn-display text-2xl">Onboard new organization</DialogTitle>
          <DialogDescription>
            Hospitals, health systems, SNFs, and home-health agencies all live in the same registry.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2" data-testid="spine-org-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={set('name')} placeholder="St. Mary's Hospital" required className="mt-1.5 h-11 rounded-xl" data-testid="spine-org-name" />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={set('type')}>
                <SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid="spine-org-type"><SelectValue /></SelectTrigger>
                <SelectContent>{ORG_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>NPI (10 digits)</Label>
              <Input value={form.npi} onChange={set('npi')} maxLength={10} className="mt-1.5 h-11 rounded-xl" data-testid="spine-org-npi" />
            </div>
            <div>
              <Label>CCN</Label>
              <Input value={form.ccn} onChange={set('ccn')} className="mt-1.5 h-11 rounded-xl" data-testid="spine-org-ccn" />
            </div>
            <div>
              <Label>Primary state</Label>
              <Select value={form.primary_state} onValueChange={set('primary_state')}>
                <SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid="spine-org-state"><SelectValue placeholder="State" /></SelectTrigger>
                <SelectContent className="max-h-64">{US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>30d baseline readmit rate (%)</Label>
              <Input
                value={form.baseline_30d_readmit_rate}
                onChange={set('baseline_30d_readmit_rate')}
                placeholder="15.6"
                className="mt-1.5 h-11 rounded-xl"
                data-testid="spine-org-readmit"
              />
            </div>
            <div className="sm:col-span-2">
              <Label>Contact email</Label>
              <Input value={form.contact_email} onChange={set('contact_email')} placeholder="ops@hospital.org" className="mt-1.5 h-11 rounded-xl" data-testid="spine-org-email" />
            </div>
          </div>
          <button type="submit" disabled={busy} className="cn-btn cn-btn-primary w-full justify-center !py-3" data-testid="spine-org-submit">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating…</> : 'Create organization'}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const OrgCard = ({ o }) => (
  <div className="cn-card rounded-2xl p-5" data-testid={`spine-org-card-${o.id}`}>
    <div className="flex items-center gap-3 mb-3">
      <div className="w-10 h-10 rounded-xl bg-cn-soft flex items-center justify-center">
        <Hospital className="w-5 h-5 text-cn-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-cn-text truncate">{o.name}</div>
        <div className="text-xs text-cn-text-muted capitalize">{o.type.replace(/_/g, ' ')}</div>
      </div>
      {o.baseline_30d_readmit_rate != null && (
        <span className="text-[11px] font-semibold px-2 py-1 rounded-full bg-amber-100 text-amber-800">
          {o.baseline_30d_readmit_rate.toFixed(1)}% readmit
        </span>
      )}
    </div>
    <div className="text-xs text-cn-text-muted space-y-1">
      {o.npi && <div>NPI: <code className="text-[11px]">{o.npi}</code></div>}
      {o.ccn && <div>CCN: <code className="text-[11px]">{o.ccn}</code></div>}
      {o.primary_state && <div>State: {o.primary_state}</div>}
      {o.contact_email && <div className="truncate">{o.contact_email}</div>}
    </div>
  </div>
);

export default function SpineAdminOrganizations() {
  const { getAuthHeader } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/organizations`, getAuthHeader());
      setOrgs(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error('Failed to load organizations');
    } finally { setLoading(false); }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  return (
    <DashboardShell
      title="Organizations"
      subtitle="Hospitals, health systems, SNFs and agencies wired into the spine."
      navItems={[]}
      testId="spine-admin-organizations"
      rightSlot={
        <button onClick={() => setModalOpen(true)} className="cn-btn cn-btn-primary" data-testid="spine-new-org-btn">
          <Plus className="w-4 h-4" /> New organization
        </button>
      }
    >
      <div className="cn-card rounded-2xl p-5 mb-6 flex items-center justify-between" data-testid="spine-orgs-summary">
        <div>
          <div className="text-3xl font-display font-semibold text-cn-text">{orgs.length}</div>
          <div className="text-sm text-cn-text-muted">organizations onboarded</div>
        </div>
        <div className="w-14 h-14 rounded-2xl bg-cn-soft flex items-center justify-center">
          <Building2 className="w-7 h-7 text-cn-primary" />
        </div>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-cn-primary" /></div>
      ) : orgs.length === 0 ? (
        <div className="cn-card rounded-3xl p-10 text-center" data-testid="spine-orgs-empty">
          <div className="w-14 h-14 rounded-2xl bg-cn-soft flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-7 h-7 text-cn-primary" />
          </div>
          <h3 className="cn-display text-2xl text-cn-text mb-2">No organizations yet</h3>
          <p className="text-cn-text-muted max-w-md mx-auto mb-6">
            Onboard your first hospital — discharge planners can then start sending referrals.
          </p>
          <button onClick={() => setModalOpen(true)} className="cn-btn cn-btn-primary" data-testid="spine-orgs-empty-btn">
            <Plus className="w-4 h-4" /> New organization
          </button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="spine-orgs-grid">
          {orgs.map((o) => <OrgCard key={o.id} o={o} />)}
        </div>
      )}

      <CreateOrgModal open={modalOpen} onClose={() => setModalOpen(false)} onCreated={load} />
    </DashboardShell>
  );
}
